import React from 'react';
import styled, { css } from 'styled-components';

import { orbitTokens, orbitType } from '../styles';

export interface IOrbitActionRowProps {
  label: string;
  /** Makes the row a button. A static row still draws the arrow, as every frame does. */
  onSelect?: () => void;
  disabled?: boolean;
  testId?: string;
}

/**
 * Suggested-action row `4952:8123` / `4952:8124`. The first row in a list pads 14 at the top and
 * every later one 12, which `:first-of-type` carries as long as the rows are siblings.
 */
const rowRules = css`
  display: flex;
  align-items: center;
  width: 100%;
  margin: 0;
  padding: 12px 14px;
  background: ${orbitTokens.surfaceAlt};
  border: 1px solid ${orbitTokens.border};
  border-radius: 10px;
  color: ${orbitTokens.textSecondary};
  font-family: inherit;
  ${orbitType.chip}
  font-weight: 400;
  text-align: left;

  &:first-of-type {
    padding-top: 14px;
  }
`;

const Row = styled.div`
  ${rowRules}
`;

const Button = styled.button`
  ${rowRules}
  cursor: pointer;

  /* figma-spec-v3.md section 4c: the hover variant of 4952:8123 is 4952:4968, which changes stroke
     and ink only. A real :hover rule, so no React state stands behind it. */
  &:hover:not(:disabled),
  &:focus-visible {
    border-color: ${orbitTokens.action};
    color: ${orbitTokens.text};
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const Label = styled.span`
  flex: 1 1 auto;
  min-width: 0;
`;

const Arrow = styled.span`
  flex: 0 0 auto;
  margin-left: 12px;
`;

const OrbitActionRow: React.FC<IOrbitActionRowProps> = ({
  label,
  onSelect,
  disabled = false,
  testId
}) => {
  const body = (
    <>
      <Label>{label}</Label>
      <Arrow aria-hidden="true">→</Arrow>
    </>
  );

  return onSelect === undefined ? (
    <Row data-testid={testId}>{body}</Row>
  ) : (
    <Button type="button" disabled={disabled} data-testid={testId} onClick={onSelect}>
      {body}
    </Button>
  );
};

export default OrbitActionRow;
